import type { ReactNode } from 'react';
import {
  formatCapturedTireSize,
  formatOemCapturedTireSize,
  labelFluidLevelForReview,
  labelLeakDripForReview,
  labelTireTypeForReview,
  type VehicleNotesFormState,
} from './vehicleNotesUtils';

function dashCell(v: string): string {
  return v.trim() || '—';
}

function labelYesNo(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

function ReviewField({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div>
      <p className="text-xs text-default-400 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-foreground whitespace-pre-wrap">{dashCell(value)}</p>
    </div>
  );
}

function ReviewSubsection({ title, children }: Readonly<{ title: string; children: ReactNode }>) {
  return (
    <div className="space-y-3">
      <p className="text-lg font-semibold text-default-700">{title}</p>
      {children}
    </div>
  );
}

export function VehicleNotesReviewSection({
  vehicleNotes,
  hideFluidAndCodes = false,
}: Readonly<{ vehicleNotes: VehicleNotesFormState; hideFluidAndCodes?: boolean }>) {
  const showModificationFields = vehicleNotes.isVehicleModified === 'yes';
  const capturedInstalledTireSize = formatCapturedTireSize(vehicleNotes);
  const capturedOemTireSize = formatOemCapturedTireSize(vehicleNotes);
  const showLiftAmount = showModificationFields && vehicleNotes.hasLiftKit === 'yes';
  const showEngineDescription = showModificationFields && vehicleNotes.hasEngineModifications === 'yes';
  const showLeakFields = vehicleNotes.hasLeaks === 'yes';
  const showCodesField = vehicleNotes.hasWarningLight === 'yes';

  return (
    <div className="bg-content1 rounded-xl border border-default-200 p-4 sm:p-6">
      <div className="space-y-6">

      

        {hideFluidAndCodes ? null : (
        <>
        <ReviewSubsection title="Fluid Section">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
            <ReviewField label="Coolant" value={labelFluidLevelForReview(vehicleNotes.coolantLevel)} />
            <ReviewField label="Condition" value={vehicleNotes.coolantCondition} />
            <ReviewField label="Engine Oil" value={labelFluidLevelForReview(vehicleNotes.engineOilLevel)} />
            <ReviewField label="Condition" value={vehicleNotes.engineOilCondition} />
            <ReviewField
              label="Transmission Fluid"
              value={labelFluidLevelForReview(vehicleNotes.transmissionFluidLevel)}
            />
            <ReviewField label="Condition" value={vehicleNotes.transmissionFluidCondition} />
            <ReviewField label="Are there any leaks present?" value={labelYesNo(vehicleNotes.hasLeaks)} />
            {showLeakFields ? (
              <>
                <ReviewField label="Where is the leak coming from?" value={vehicleNotes.leakLocation} />
                <ReviewField
                  label="Drip Condition"
                  value={labelLeakDripForReview(vehicleNotes.leakDripCondition)}
                />
              </>
            ) : null}
          </div>
        </ReviewSubsection>

        <ReviewSubsection title="Codes Section">
          <div className="space-y-3">
            <ReviewField
              label="Is there a warning light illuminated?"
              value={labelYesNo(vehicleNotes.hasWarningLight)}
            />
            {showCodesField ? (
              <ReviewField label="Codes and Definitions" value={vehicleNotes.codesAndDefinitions} />
            ) : null}
          </div>
        </ReviewSubsection>
        </>
        )}
        <ReviewSubsection title="Parts and labor warranty on recommended repair ?">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
            <ReviewField label="Months" value={vehicleNotes.partsLaborWarrantyMonths} />
            <ReviewField label="Miles" value={vehicleNotes.partsLaborWarrantyMiles} />
          </div>
        </ReviewSubsection>
        <ReviewSubsection title="Vehicle Modification">
          <ReviewField label="Is the vehicle modified?" value={labelYesNo(vehicleNotes.isVehicleModified)} />
          {showModificationFields ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4">
              <p className="sm:col-span-2 text-xs font-semibold uppercase tracking-wide text-default-500 underline">
                Installed Tire Size
              </p>
              <ReviewField label="Type" value={labelTireTypeForReview(vehicleNotes.tireType)} />
              <ReviewField label="Section Width (mm)" value={vehicleNotes.tireSectionWidthMm} />
              <ReviewField label="Aspect Ratio" value={vehicleNotes.tireAspectRatio} />
              <ReviewField label="Wheel Diameter (in)" value={vehicleNotes.tireWheelDiameterIn} />
              <ReviewField label="Captured tire size" value={capturedInstalledTireSize} />
              <p className="sm:col-span-2 text-xs font-semibold uppercase tracking-wide text-default-500 pt-2 underline">
                OEM Tire Size
              </p>
              <ReviewField label="Section Width (mm)" value={vehicleNotes.oemTireSectionWidthMm} />
              <ReviewField label="Aspect Ratio" value={vehicleNotes.oemTireAspectRatio} />
              <ReviewField label="Wheel Diameter (in)" value={vehicleNotes.oemTireWheelDiameterIn} />
              <ReviewField label="Captured OEM tire size" value={capturedOemTireSize} />
              <ReviewField
                label="Is the vehicle lifted/lowered?"
                value={labelYesNo(vehicleNotes.hasLiftKit)}
              />
              {showLiftAmount ? (
                <ReviewField
                  label="Estimated Inches Lowered or Lifted"
                  value={vehicleNotes.liftAmountInches}
                />
              ) : null}
              <ReviewField
                label="Are there Engine or drivertrain modifications? For example: Tuner, turbo, super-charged, etc."
                value={labelYesNo(vehicleNotes.hasEngineModifications)}
              />
              {showEngineDescription ? (
                <div className="sm:col-span-2">
                  <ReviewField
                    label="List modifications"
                    value={vehicleNotes.engineModificationDescription}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </ReviewSubsection>
      </div>
    </div>
  );
}
